import { useEffect, useState } from 'react';
import { api } from '../../../api/client';
import AnnouncementForm from './AnnouncementForm';
import styles from './AnnouncementsPage.module.css';

const priorityColors: Record<string, string> = {
  URGENT: '#d00',
  HIGH: '#e67e22',
  NORMAL: '#0288d1',
  LOW: '#888',
};

const priorityLabels: Record<string, string> = {
  URGENT: 'Urgente',
  HIGH: 'Alta',
  NORMAL: 'Normal',
  LOW: 'Baja',
};

export default function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [filterPriority, setFilterPriority] = useState('');

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filterPriority) params.priority = filterPriority;
      const data = await api.get<any[]>('/api/communication/announcements', { params });
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch {
      /* ignore */
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [filterPriority]);

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este anuncio?')) return;
    try {
      await api.delete(`/api/communication/announcements/${id}`);
      if (selected?.id === id) setSelected(null);
      fetchAnnouncements();
    } catch {
      alert('Error al eliminar');
    }
  };

  const handleMarkRead = async (id: string) => {
    try {
      await api.post(`/api/communication/announcements/${id}/read`);
      fetchAnnouncements();
    } catch {
      /* ignore */
    }
  };

  const userHasRead = (ann: any, userId?: string) => {
    if (!userId || !ann.readBy) return false;
    return ann.readBy.some((r: any) => r.userId === userId);
  };

  const currentUserId = (() => {
    try {
      const token = localStorage.getItem('auth-token');
      if (!token) return '';
      return JSON.parse(atob(token.split('.')[1])).userId || '';
    } catch {
      return '';
    }
  })();

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <div>
          <h1>Anuncios</h1>
          <p className={styles.subtitle}>Comunicados y avisos importantes</p>
        </div>
        <button className={styles.addButton} onClick={() => setShowModal(true)}>
          + Nuevo Anuncio
        </button>
      </div>

      <div className={styles.filterRow}>
        <label>Filtrar por prioridad:</label>
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          className={styles.filterSelect}
        >
          <option value="">Todas</option>
          <option value="URGENT">Urgente</option>
          <option value="HIGH">Alta</option>
          <option value="NORMAL">Normal</option>
          <option value="LOW">Baja</option>
        </select>
      </div>

      <div className={styles.grid}>
        {loading ? (
          <div className={styles.loading}>Cargando...</div>
        ) : announcements.length === 0 ? (
          <div className={styles.empty}>No hay anuncios</div>
        ) : (
          announcements.map((ann) => {
            const isRead = userHasRead(ann, currentUserId);
            return (
              <div
                key={ann.id}
                className={`${styles.card} ${!isRead ? styles.unread : ''} ${selected?.id === ann.id ? styles.selected : ''}`}
                onClick={() => setSelected(ann)}
              >
                <div className={styles.cardHeader}>
                  <span
                    className={styles.priority}
                    style={{ color: priorityColors[ann.priority] || '#0288d1' }}
                  >
                    {priorityLabels[ann.priority] || ann.priority}
                  </span>
                  <span className={styles.date}>
                    {new Date(ann.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <h3 className={styles.cardTitle}>{ann.title}</h3>
                <p className={styles.cardContent}>{ann.content}</p>
                <div className={styles.cardFooter}>
                  <span className={styles.author}>Por: {ann.author?.name || 'Desconocido'}</span>
                  <div className={styles.cardActions}>
                    {!isRead && (
                      <button
                        className={styles.readBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkRead(ann.id);
                        }}
                      >
                        Marcar leído
                      </button>
                    )}
                    <button
                      className={styles.deleteBtnSmall}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(ann.id);
                      }}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {showModal && (
        <AnnouncementForm
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            fetchAnnouncements();
          }}
        />
      )}
    </div>
  );
}
